import { Spacing } from '@persona-id/design-tokens';
import { FC, useMemo, VFC } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import ReactSelect from 'dashboard/components/ReactSelect';
import SearchInput from 'dashboard/components-lib/SearchInput';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { InquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { ThemeSet } from 'dashboard/models/theme-set';
import { RootState } from 'dashboard/redux/store';
import { Z_INDICES } from 'dashboard/styles/variables';
import { partitionInUseInquiryTemplates } from 'dashboard/views/DashboardThemeSetsEditorView/util';
import DropdownMenu from 'design-system/components/DropdownMenu';
import Spacer from 'design-system/components/Spacer';
import Stack from 'design-system/components/Stack/Stack';
import { Label, Paragraph } from 'design-system/components/Text';
import { INQUIRY_LANGUAGES_ENGLISH, InquiryIso6391LanguageCode } from 'lib/constants/locales';
import useToggle from 'lib/hooks/useToggle';

const TitleContainer = styled(Stack)`
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
  padding: ${Spacing[5]} ${Spacing[4]};
`;

const StyledReactSelect = styled(ReactSelect<string>)`
  min-width: 350px;
`;

const AdditionalInfoText = styled(Paragraph)`
  font-size: ${({ theme }) => theme.text.paragraph.md.fontSize};
  color: ${({ theme }) => theme.color.text.primary};
`;

const StyledText = styled('span')`
  font-size: ${({ theme }) => theme.text.paragraph.md.fontSize};
  color: ${({ theme }) => theme.color.text.brand};
  cursor: pointer;
`;

export interface ThemeSetEditorTemplatePreviewControlProps {
  themeSet: ThemeSet;
  inquiryTemplates: InquiryTemplate[];
  /** The ITV being used for the theme set editor preview */
  inquiryTemplateVersion: InquiryTemplateVersion;
  handleInquiryTemplateChange: (templateId: string) => Promise<void>;
  /** The locale being used for the theme set editor preview */
  locale: InquiryIso6391LanguageCode;
  handleLocaleChange: (locale: InquiryIso6391LanguageCode) => Promise<void>;
}

interface SearchBarProps {
  searchTerm: string;
  handleSetSearchTerm: (searchTerm: string) => void;
}

export const ThemeSetEditorTemplatePreviewControl: FC<
  ThemeSetEditorTemplatePreviewControlProps & SearchBarProps
> = ({
  themeSet,
  inquiryTemplates,
  inquiryTemplateVersion,
  handleInquiryTemplateChange,
  locale,
  handleLocaleChange,
  searchTerm,
  handleSetSearchTerm,
}) => {
  const orgDefaultThemeSetId = useSelector<RootState, string | null>(
    (state) => state.organization.organization?.relationships['theme-set'].data?.id,
  );

  const inquiryTemplatesOptions = useMemo(() => {
    const [inUseTemplates, notInUseTemplates] = partitionInUseInquiryTemplates(
      themeSet,
      inquiryTemplates,
      { sortByNameDisplay: true },
    );
    const inUseTemplateOptions = inUseTemplates.map((inqTemp) => ({
      value: inqTemp.id,
      label: inqTemp.attributes['name-display'],
    }));
    const notInUseTemplateOptions = notInUseTemplates.map((inqTemp) => ({
      value: inqTemp.id,
      label: inqTemp.attributes['name-display'],
    }));

    if (inUseTemplateOptions.length > 0) {
      return [
        {
          label: 'Templates using theme as default',
          options: inUseTemplateOptions,
        },
        {
          label: 'Other templates',
          options: notInUseTemplateOptions,
        },
      ];
    } else {
      return notInUseTemplateOptions;
    }
  }, [inquiryTemplates, themeSet]);

  const languageOptions = useMemo(
    () =>
      inquiryTemplateVersion.attributes['enabled-locales'].map((enabledLocale) => ({
        value: enabledLocale,
        label: INQUIRY_LANGUAGES_ENGLISH[enabledLocale],
      })),
    [inquiryTemplateVersion],
  );

  return (
    <TitleContainer direction='vertical' spacing={Spacing[4]}>
      {orgDefaultThemeSetId === themeSet.id ? (
        <>
          <AdditionalInfoText>This theme is the organization default</AdditionalInfoText>
        </>
      ) : (
        <InUseInfoText themeSet={themeSet} inquiryTemplates={inquiryTemplates} />
      )}
      <div>
        <Label size='md' weight='bold' id='edit-inquiry-template-preview-dropdown'>
          Inquiry Template Previews
        </Label>
        <Spacer height={1} />
        <StyledReactSelect
          id='edit-inquiry-template-preview-dropdown'
          aria-labelledby='edit-inquiry-template-preview-dropdown'
          value={inquiryTemplateVersion.relationships['inquiry-template'].data.id}
          options={inquiryTemplatesOptions}
          onChange={handleInquiryTemplateChange}
        />
      </div>
      <div>
        <Label size='md' weight='bold' id='edit-locale-language-preview-dropdown'>
          Languages
        </Label>
        <Spacer height={1} />
        <StyledReactSelect
          id='edit-locale-language-preview-dropdown'
          aria-labelledby='edit-locale-language-preview-dropdown'
          value={locale}
          options={languageOptions}
          onChange={handleLocaleChange}
        />
      </div>
      <div>
        <Spacer height={1} />
        <SearchInput
          aria-labelledby='edit-attribute-search-bar'
          id='edit-attribute-search-bar'
          size='md'
          title='Search Attributes'
          value={searchTerm}
          handleInput={handleSetSearchTerm}
          placeholder='Attribute name / description'
        />
      </div>
    </TitleContainer>
  );
};

interface InUseInfoTextProps {
  themeSet: ThemeSet;
  inquiryTemplates: InquiryTemplate[];
}

const InUseInfoText: VFC<InUseInfoTextProps> = ({ themeSet, inquiryTemplates }) => {
  const [dropdownOpened, { enable: openDropdown, disable: hideDropdown }] = useToggle(false);
  const navigate = useNavigate();
  const associatedInquiryTemplates = useMemo(() => {
    // filter templates that use the currently selected theme as default.
    const [inUseInquiryTemplates] = partitionInUseInquiryTemplates(themeSet, inquiryTemplates, {
      sortByNameDisplay: true,
    });

    // construct count string (singular for count of 1, plural otherwise)
    const templateCount = inUseInquiryTemplates.length;
    const templateNounForm = templateCount <= 1 ? 'template' : 'templates';
    const countLabel = ` ${templateCount} inquiry ${templateNounForm}`;

    const templateLinks = inUseInquiryTemplates.map((template, idx) => {
      const partialUrl = `${template.id}/${template.relationships['latest-draft-version'].data?.id ?? template.relationships['latest-published-version'].data?.id}/flow`;

      return (
        <DropdownMenu.Item
          id={`themeSet-card__template-dropdown-${idx}`}
          key={`themeSet-card__template-dropdown-${idx}`}
          variant='basic'
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/dashboard/inquiry-templates/${partialUrl}`);
          }}
        >
          <StyledText>{template.attributes['name-display']}</StyledText>
        </DropdownMenu.Item>
      );
    });

    return { countLabel, templateLinks };
  }, [themeSet.relationships['inquiry-templates'].data, inquiryTemplates]);

  return (
    <>
      <AdditionalInfoText>
        {associatedInquiryTemplates.templateLinks.length === 0 ? (
          <span>This theme is not being used by any template</span>
        ) : (
          <>
            This theme is used by
            <DropdownMenu
              contentProps={{
                onEscapeKeyDown: hideDropdown,
                onInteractOutside: hideDropdown,
              }}
              open={dropdownOpened}
              onOpenChange={(opened) => opened && openDropdown()}
              side='bottom'
              align='center'
              zIndex={Z_INDICES.topNavTooltip}
              trigger={
                <StyledText
                  onMouseEnter={openDropdown}
                  id={`theme-set-card_template-menu-${themeSet.id}`}
                >
                  {associatedInquiryTemplates.countLabel}
                </StyledText>
              }
            >
              {associatedInquiryTemplates.templateLinks}
            </DropdownMenu>
          </>
        )}
      </AdditionalInfoText>
    </>
  );
};

export default ThemeSetEditorTemplatePreviewControl;
